import { Box } from "@mui/material";
import { lighten } from "@mui/material/styles";
import type { DateTime } from "luxon";
import { GRID_FONT, GRID_TINT, GRID_Z } from "../../../../theme/tokens";
import { mapCalendarCells, ROW_H } from "../gridGeometry";

type Props = {
	weeks: number;
	/** First day of week 1 (cycle anchor) — drives the Sun–Sat labels. */
	cycleStart: DateTime;
	/**
	 * Leading-width variant the grid is using. `standard` and `compact` render
	 * IDENTICALLY here (the day strip is width-independent) — both are the Coverage
	 * look: weekend tint + uppercase, spaced, bold labels. `people` is the flatter
	 * People look (no weekend tint, lighter/looser) — kept EXACTLY as today.
	 */
	variant: "standard" | "compact" | "people";
	/** Show exact dates (M/d) instead of day of week (ccc). Only applies to people variant. */
	showExactDate?: boolean;
};

/** Row 2 (day columns) of every v3 grid: the Sun–Sat strip + per-week dividers. */
export const GridDayHeaderRow = ({ weeks, cycleStart, variant, showExactDate = false }: Props) => {
	const isPeople = variant === "people";
	return (
		<>
			{mapCalendarCells(
				weeks,
				(_w, d, idx) => {
					const date = cycleStart.plus({ days: idx });
					// Sat/Sun read one notch softer so the eye locks onto the week's shape.
					const isWeekend = d === 0 || d === 6;
					const label = isPeople && showExactDate ? date.toFormat("M/d") : date.toFormat("ccc");
					return (
						<Box
							role="columnheader"
							data-week={_w}
							sx={(theme) => ({
								position: "sticky",
								top: ROW_H,
								zIndex: GRID_Z.DAY_HEADER,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								py: isPeople ? 0.5 : 0.625,
								backgroundColor: !isPeople && isWeekend ? lighten(theme.palette.primary.main, GRID_TINT.WEEKEND) : theme.palette.primary.main,
								color: theme.palette.primary.contrastText,
								fontWeight: isPeople ? 600 : 700,
								fontSize: GRID_FONT.SM,
								lineHeight: 1.1,
								...(isPeople ? {} : { letterSpacing: "0.03em", textTransform: "uppercase" }),
							})}
						>
							{label}
						</Box>
					);
				},
				() => (
					<Box role="presentation" sx={(theme) => ({ position: "sticky", top: ROW_H, backgroundColor: theme.palette.primary.main })} />
				),
			)}
		</>
	);
};
